import type { ReactNode } from 'react'
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom'
import { LogOut } from 'lucide-react'
import { useAuth } from '../../hooks/useAuth'
import { useAuthModal } from '../../hooks/authModalContext'
import { useAssertVertical, verticalThemeVars } from '../layout/useVertical'
import { SwitchServiceButton } from '../layout/SwitchServiceButton'
import { VerticalBrand } from '../layout/VerticalBrand'
import { AppShell } from '../layout/AppShell'
import { NotificationBell } from '../layout/NotificationBell'
import { RoleSwitcherButton } from '../layout/RoleSwitcher'
import { ThriftSearchBar } from './ThriftSearchBar'
import {
  THRIFT_DISCOVERY_PATH,
  ThriftSearchContext,
  useThriftSearch,
  useThriftSearchState,
} from './ThriftSearchContext'

/** The search pill, bound to the shared thrift search context. */
function HeaderSearch() {
  const { search, setSearch } = useThriftSearch()
  const { pathname } = useLocation()
  const navigate = useNavigate()
  const onDiscovery = pathname === THRIFT_DISCOVERY_PATH
  // Off the results page (an item's detail view) there is nothing to filter live, so
  // the pill collects a query and commits it — and the results are one route away.
  return (
    <ThriftSearchBar
      search={search}
      deferred={!onDiscovery}
      onChange={(next) => {
        setSearch(next)
        if (!onDiscovery) navigate(THRIFT_DISCOVERY_PATH)
      }}
    />
  )
}

/** Thrifts-specific public header — no discovery tabs; the search pill sits in the
 *  header row next to the logo and sign-in. */
function ThriftPublicHeader() {
  const { isAuthenticated, logout } = useAuth()
  const { openAuthModal } = useAuthModal()

  return (
    <header className="sticky top-0 z-30 border-b border-stone-200 bg-white/95 backdrop-blur">
      {/* Full-bleed to match AppShell — see PublicHeader. */}
      <div className="grid w-full grid-cols-[auto_auto] items-center gap-3 px-4 py-3 md:grid-cols-[auto_minmax(0,1fr)_auto] md:px-6">
        <Link
          to={'/thrifts'}
          title="Thrifts home"
          aria-label="Thrifts home"
          className="flex items-center gap-3 text-stone-950"
        >
          <VerticalBrand vertical="thrifts" />
        </Link>
        <div className="col-span-2 row-start-2 min-w-0 md:col-span-1 md:row-start-auto">
          <HeaderSearch />
        </div>
        {isAuthenticated ? (
          <div className="flex items-center justify-end gap-3">
            <div className="hidden sm:block">
              <RoleSwitcherButton />
            </div>
            <SwitchServiceButton />
            <NotificationBell />
            <button
              onClick={logout}
              className="hidden items-center gap-2 rounded-xl border border-stone-300 px-3 py-1.5 text-xs font-semibold text-stone-700 hover:bg-stone-100 sm:inline-flex"
            >
              <LogOut className="h-4 w-4" />
              Log out
            </button>
          </div>
        ) : (
          <div className="flex items-center justify-end gap-3">
            <SwitchServiceButton />

            <button
              type="button"
              onClick={openAuthModal}
              className="rounded-xl border border-stone-300 px-3 py-1.5 text-xs font-semibold text-stone-700 hover:bg-stone-100"
            >
              Sign in
            </button>
          </div>
        )}
      </div>
    </header>
  )
}

/**
 * Route layout owning the search shared by every thrift surface.
 *
 * Above the routes rather than inside the frame, so the query survives opening an
 * item and pressing back — a frame per page would rebuild it empty each time.
 */
export function ThriftSearchLayout() {
  const searchValue = useThriftSearchState()
  return (
    <ThriftSearchContext.Provider value={searchValue}>
      <Outlet />
    </ThriftSearchContext.Provider>
  )
}

/**
 * Page frame for thrift discovery. Replaces the shared discovery header (with its
 * single "Thrifts" tab) with a thrifts header that carries the search pill in the
 * top row. Authenticated users keep the app sidebar (AppShell); the search pill
 * sits at the top of the workspace. Mirrors HomestayDiscoveryFrame.
 */
export function ThriftDiscoveryFrame({ children }: { children: ReactNode }) {
  const { isAuthenticated } = useAuth()
  // Entering thrift surfaces activates the thrifts vertical so the chrome (and the
  // AppShell path for authenticated users) themes sky, even via deep links.
  useAssertVertical('thrifts')

  return isAuthenticated ? (
    <AppShell showWorkspaceBanner={false} headerCenter={<HeaderSearch />}>
      <div className="space-y-4">{children}</div>
    </AppShell>
  ) : (
    <div className="min-h-screen bg-stone-100" style={verticalThemeVars('thrifts')}>
      <ThriftPublicHeader />
      <main className="mx-auto max-w-[1500px] px-4 py-4 md:px-6 md:py-6">
        <div className="space-y-4">{children}</div>
      </main>
    </div>
  )
}
